<template>
  <div id="app" :class="{ 'bg-neutral-200': !$route.meta.noBackground }">
    <!-- eslint-disable-next-line vue/no-bare-strings-in-template -- admin only -->
    <div v-if="isSuperUser" :class="$style.superUserFrame">
      Signed in as super user
    </div>
    <Banner
      v-if="
        activeUser?.organisationDeactivated && activeSessionType === 'Maker'
      "
    >
      <p>
        <i18next :translation="$t('landing-page:partnerDeactivatedBanner')">
          <template #email>
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -- email address doesn't need to be translated -->
            <a href="mailto:support@cutr.com">support@cutr.com</a>
          </template>
        </i18next>
      </p>
    </Banner>
    <UnconfirmedEmailBanner v-if="showEmailUnconfirmedBanner" />
    <StaleVersionBanner />
    <Navigation v-if="showChrome" />
    <main
      v-if="!isMaintenanceModeEnabled"
      id="main-view"
      :class="{
        [$style.offsetnav]: showChrome,
        [$style.loading]: !$i18next.isInitialized,
        [$style.home]: currentRoute.name === 'home',
      }"
      :data-route-name="currentRoute.name"
    >
      <router-view v-if="$i18next.isInitialized" :key="$route.params.id" />
      <!-- eslint-disable-next-line vue/no-bare-strings-in-template -- this is before i18next is initialized -->
      <div v-else class="flex flex-col items-center justify-center">
        Loading <LoadingDots />
      </div>
    </main>
    <CutrFooter v-if="showChrome" />

    <ModalGlobal />
    <ModalConfirm />
    <ModalBouwvak />
    <MaintenanceScreen v-if="isMaintenanceModeEnabled" />
    <ProductFruits
      v-if="
        !isDev &&
        productFruitsEnabled &&
        isLoggedIn &&
        (activeUser.locale || $i18next.resolvedLanguage)
      "
      projectCode="hh6VZ5bT1SPTpJyB"
      :language="activeUser.locale || $i18next.resolvedLanguage"
      :username="activeUser.name"
      :email="activeUser.email"
      :firstname="activeUser.firstName"
      :lastname="activeUser.lastName"
      :signUpAt="activeUser.createdAt"
      :role="activeUser.role"
      :props="productFruitsProps"
    />
    <VueQueryDevtools v-if="isDev" />
  </div>
  <Toaster richColors closeButton />
</template>

<script>
import Navigation from './components/pages/Navigation.vue';
import Banner from '@/components/elements/Banner';
import CutrFooter from './components/pages/Footer.vue';
import ModalConfirm from './components/elements/ModalConfirm.vue';
import ModalGlobal from './components/elements/ModalGlobal.vue';
import ModalBouwvak from './components/elements/ModalBouwvak.vue';
import MaintenanceScreen from './components/elements/MaintenanceScreen.vue';
import UnconfirmedEmailBanner from './components/elements/UnconfirmedEmailBanner.vue';
import StaleVersionBanner from './components/elements/StaleVersionBanner.vue';
import { useActiveUserStore } from '@/stores/user';
import { mapState } from 'pinia';
import ProductFruits from './components/common/ProductFruits.vue';
import { useFlag } from '@/composables/useFlag';
import { useMaintenanceMode } from '@/composables/useMaintenanceMode';
import { defineAsyncComponent } from 'vue';

const isDev = import.meta.env.DEV;
const Env = import.meta.env.VITE_CUTR_ENV;

export default {
  name: 'App',

  components: {
    Navigation,
    ModalGlobal,
    ModalConfirm,
    ModalBouwvak,
    MaintenanceScreen,
    CutrFooter,
    UnconfirmedEmailBanner,
    StaleVersionBanner,
    ProductFruits,
    Banner,
    ...(isDev && {
      VueQueryDevtools: defineAsyncComponent(() =>
        import('@tanstack/vue-query-devtools').then((m) => m.VueQueryDevtools)
      ),
    }),
  },

  setup() {
    const productFruitsEnabled = useFlag('product-fruits');
    const maintenanceModeActive = useMaintenanceMode();
    return {
      productFruitsEnabled,
      maintenanceModeActive,
    };
  },

  data() {
    return {
      bypassMaintenanceMode: false,
      intercomCheckAttempts: 0,
      hasIntercom: false,
      productFruitsProps: { Env },
      isDev,
    };
  },

  computed: {
    currentRoute() {
      return this.$route;
    },

    isMaintenanceModeEnabled() {
      if (this.currentRoute.name === 'signin') return false;
      if (this.bypassMaintenanceMode) return false;
      return this.maintenanceModeActive;
    },

    showChrome() {
      return (
        !this.isMaintenanceModeEnabled &&
        !this.currentRoute.meta?.disableAppChrome &&
        this.$i18next.isInitialized
      );
    },

    showEmailUnconfirmedBanner() {
      return this.activeUser.emailStatus === 'unconfirmed' && this.isLoggedIn;
    },
    ...mapState(useActiveUserStore, [
      'superUserId',
      'activeUser',
      'isLoggedIn',
      'activeSessionType',
    ]),
    isSuperUser() {
      return Boolean(this.superUserId);
    },
  },

  watch: {
    '$i18next.resolvedLanguage': {
      immediate: true,
      handler(lang) {
        this.$formkit.setLocale(lang);
      },
    },
  },

  mounted: function () {
    const getCookie = (name) =>
      document.cookie
        .split('; ')
        .find((row) => row.startsWith(name))
        ?.split('=')[1];

    const eraseCookie = (name) => {
      const pastDate = new Date(0);
      document.cookie =
        name +
        '=; expires=' +
        pastDate.toUTCString() +
        '; path=/; domain=.cutr.com';
    };

    const rawCookie = getCookie('tracking-preferences');
    if (!rawCookie) return;

    const decodedCookie = decodeURIComponent(rawCookie);
    let cookie;

    try {
      cookie = JSON.parse(decodedCookie);
    } catch (err) {
      console.error(err);
    }

    if (cookie?.integrations?.['All'] === false) {
      eraseCookie('tracking-preferences');
      this.$track('consent-manager:segment-bad-cookie', { cookie });
      this.$forceUpdate();
    }

    this.trackIntercom();
  },

  methods: {
    trackIntercom() {
      if (this.hasIntercom) {
        window.Intercom?.('onShow', () => {
          this.$track('intercom:shown', {
            page: this.$route.name,
          });
        });

        window.intercomSettings = {
          ...(window.intercomSettings || {}),
          vertical_padding: 104,
        };
        return;
      }

      if (this.intercomCheckAttempts > 10) return;

      setTimeout(() => {
        this.hasIntercom = Boolean(window.Intercom);
        this.intercomCheckAttempts = this.intercomCheckAttempts + 1;
        this.trackIntercom();
      }, this.intercomCheckAttempts * 1000);
    },

    closeBanner() {
      this.showBanner = false;
    },
  },
};
</script>

<style module>
.offsetnav {
  position: relative;
  min-height: calc(100vh - var(--footerHeight));
  padding-top: var(--navbarHeight);
}

.loading {
  display: flex;
  min-height: 100vh;
  justify-content: center;
  align-items: center;
}

/* The launchpad lets the header scroll away on small screens, so the page drops its offset. */
@media (max-width: 860px) {
  .home {
    --navbarHeight: 0;
  }
}

.superUserFrame {
  position: fixed;
  inset: 0;
  z-index: 901;
  pointer-events: none;
  padding: 10px 0 0 150px;
  border: 10px solid red;
  color: red;
  font-size: large;
  font-weight: 500;
  animation: pulse 1.5s linear infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.4;
  }

  40%,
  60% {
    opacity: 1;
  }
}
</style>
